import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;


// Send API calls to whatever host loaded the app (apex, www, emergent.host) so the
// auth cookie is always first-party; /api is proxied on every host.
axios.interceptors.request.use((cfg) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  if (API_URL && origin && origin !== API_URL && typeof cfg.url === 'string' && cfg.url.startsWith(API_URL)) {
    cfg.url = origin + cfg.url.slice(API_URL.length);
  }
  return cfg;
});

// On a 401 from any API call, refresh the access token once and retry the request.
let _refreshing = null;
axios.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config;
  const url = cfg?.url || '';
  if (error.response?.status !== 401 || !cfg || cfg._retried || url.includes('/api/auth/')) {
    return Promise.reject(error);
  }
  cfg._retried = true;
  try {
    _refreshing = _refreshing || axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
    await _refreshing;
  } catch (_) {
    if (error.response?.data) error.response.data.detail = 'Your login has expired. Please log out and log back in, then try again.';
    return Promise.reject(error);
  } finally {
    _refreshing = null;
  }
  return axios({ ...cfg, withCredentials: true });
});

// Helper to format API errors
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = authed
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (err) {
      // If access token is expired, try the refresh endpoint once and retry /me
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        const retry = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(retry.data);
      } catch (_) {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true };
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const register = async (email, password, name) => {
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true };
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    }
    setUser(false);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    loginWithGoogle,
    processGoogleSession,
    checkAuth,
    setUser,
    isAuthenticated: !!user && user !== false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
